import type { JSX } from 'react';
import { TaxCodePicker } from '@pflegenavi/web-components';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import type { TaxCodeResultDto } from '@pflegenavi/service-provider-shared/api-endpoints';

interface FServiceTaxCodePickerProps<
  T extends string,
  V extends { [key in T]?: string[] }
> {
  name: T;
  formik: FormikProps<V>;
  taxCodes: TaxCodeResultDto[];
  label: string;
  id?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

function formatTaxCodeResultLabel(tc: TaxCodeResultDto): string {
  const suffix = tc.paragraph ? `: ${tc.paragraph}` : '';
  return `${tc.name}${suffix} (${tc.taxRatio}%)`;
}

export const FServiceTaxCodePicker = <
  Field extends string,
  V extends { [key in Field]?: string[] }
>({
  name,
  formik,
  taxCodes,
  label,
  id,
  fullWidth,
  size,
  disabled,
}: FServiceTaxCodePickerProps<Field, V>): JSX.Element => {
  const fieldValue = getIn(formik.values, name) as string[] | undefined;
  const fieldError = getIn(formik.errors, name);
  const fieldTouched = getIn(formik.touched, name);
  const showError = Boolean(fieldTouched && typeof fieldError === 'string');

  return (
    <TaxCodePicker
      id={id}
      name={name}
      label={label}
      taxCodes={taxCodes}
      getOptionLabel={formatTaxCodeResultLabel}
      value={fieldValue?.[0] ?? null}
      onChange={(tc) => {
        formik.setFieldValue(name, tc ? [tc.id] : []);
      }}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || formik.isSubmitting}
      error={showError}
      helperText={showError ? fieldError : undefined}
    />
  );
};
